import { useEffect, useRef, useState } from "react"
import type { ColorCardData } from "../types"
import { useCart } from "../cart-context"

// Accept either a string ("19.99") or a number (19.99) and render "$19.99".
function formatPrice(raw: string | number | undefined): string | null {
  if (raw == null) return null
  if (typeof raw === "string" && raw.startsWith("$")) return raw
  const n = typeof raw === "number" ? raw : parseFloat(raw)
  if (!Number.isFinite(n)) return null
  return `$${n.toFixed(2)}`
}

/** Color match card — swatch-first tile sized for the dark canvas
 *  background. The swatch dominates (3:2 aspect) so it reads as a
 *  color visualization, not a generic product card. Code + name +
 *  brand sit underneath. Tap-through to product page when available.
 *  When variants are present, renders an interactive section (pills +
 *  price + add-to-cart) inside a <div> wrapper — never inside an <a>. */
export function ColorCard({ data }: { data: ColorCardData }) {
  const { addToCart } = useCart()
  const [imgFailed, setImgFailed] = useState(false)

  // ── variant state (mirrors ProductCard pattern) ──────────────────────
  const variants = data.variants ?? []
  const [selIdx, setSelIdx] = useState(() =>
    Math.max(0, variants.findIndex((v) => v.available)),
  )
  // Reset to first-available when the variant set changes (e.g. card reuse).
  const variantKey = variants.map((v) => v.id).join(",")
  useEffect(() => {
    setSelIdx(Math.max(0, variants.findIndex((v) => v.available)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantKey])
  // Preference order for what fills the color tile (highest first):
  //   1. matte speed-shape render — the actual paint on a 3D shape
  //   2. gloss speed-shape render — same but glossy finish
  //   3. flat swatch image from the merchant (rare)
  //   4. rgbHex fill (the MCC OEM case — most common)
  //   5. neutral grey fallback
  // The speed-shape preference was added 2026-06-03 per Justin's "use
  // matte or gloss speed-shapes with products if available" feedback.
  const heroSrc =
    (!imgFailed && (data.renders?.matte ?? data.renders?.gloss)) ||
    (!imgFailed && data.swatchUrl) ||
    null
  const showHero = !!heroSrc
  const swatchFill =
    data.rgbHex && /^#[0-9a-f]{3,8}$/i.test(data.rgbHex)
      ? data.rgbHex
      : "#3a3a4a"
  // Speed-shape renders are shot on light backdrops — keep the tile's
  // backdrop light when we're using a render so it doesn't fight the
  // photo. Use the rgbHex fill only when there's no hero image.
  const tileBackground = data.renders?.matte || data.renders?.gloss
    ? "radial-gradient(120% 100% at 50% 0%, #ffffff 0%, #f3f5f8 70%, #e6eaf0 100%)"
    : swatchFill

  const inner = (
    <>
      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "3 / 2",
          background: tileBackground,
          borderRadius: 10,
          overflow: "hidden",
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.10)",
        }}
      >
        {showHero && (
          <img
            src={heroSrc!}
            alt={`${data.name} ${data.renders?.matte || data.renders?.gloss ? "render" : "swatch"}`}
            loading="lazy"
            onError={() => setImgFailed(true)}
            style={{
              width: "100%",
              height: "100%",
              // Speed-shape renders look better contained (whole shape
              // visible on the light backdrop); flat swatches look better
              // cover-fitted (no awkward edge gaps).
              objectFit: data.renders?.matte || data.renders?.gloss ? "contain" : "cover",
              display: "block",
            }}
          />
        )}
      </div>
      <div
        style={{
          padding: "10px 12px 12px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "rgba(255,255,255,0.96)",
            lineHeight: 1.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={data.name}
        >
          {data.name}
        </div>
        <div
          style={{
            fontSize: 11.5,
            color: "rgba(255,255,255,0.65)",
            display: "flex",
            gap: 6,
            alignItems: "baseline",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
              fontWeight: 600,
              color: "rgba(255,255,255,0.85)",
            }}
          >
            {data.code}
          </span>
          {data.brand && <span>· {data.brand}</span>}
          {data.finish && <span>· {data.finish}</span>}
        </div>
      </div>
    </>
  )

  const wrapperStyle = {
    display: "block",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 14,
    overflow: "hidden",
    color: "inherit",
    textDecoration: "none",
    transition: "background 120ms ease, border-color 120ms ease",
  } as const

  // ── interactive path — variants present ──────────────────────────────
  // NOT wrapped in <a> to keep buttons out of anchor (invalid HTML).
  if (variants.length > 0) {
    const sel = variants[selIdx] ?? variants[0]
    const price = formatPrice(sel?.price)
    const fits = data.fitment?.[0]

    // Add-to-cart button state: idle → adding (spinner) → added (check) → idle
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [cartState, setCartState] = useState<"idle" | "adding" | "added">("idle")
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const revertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Respect prefers-reduced-motion for the scale pulse.
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches

    const handleAddToCart = async () => {
      if (!sel?.available || cartState !== "idle") return
      setCartState("adding")
      try {
        await addToCart(sel.id, 1)
      } finally {
        setCartState("added")
        if (revertTimerRef.current) clearTimeout(revertTimerRef.current)
        revertTimerRef.current = setTimeout(() => setCartState("idle"), 1500)
      }
    }

    // Clean up revert timer on unmount.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      return () => {
        if (revertTimerRef.current) clearTimeout(revertTimerRef.current)
      }
    }, [])

    const isAdded = cartState === "added"
    const isAdding = cartState === "adding"
    const canAdd = !!(sel?.available) && cartState === "idle"

    const addBtnStyle: React.CSSProperties = {
      padding: "8px 14px",
      fontSize: 13,
      fontWeight: 600,
      borderRadius: 8,
      border: "none",
      background: isAdded
        ? "#4caf86"
        : canAdd
          ? "#6DC3F7"
          : "rgba(255,255,255,0.15)",
      color: canAdd || isAdded || isAdding ? "#fff" : "rgba(255,255,255,0.4)",
      cursor: canAdd ? "pointer" : "not-allowed",
      transition: "background 200ms",
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      // Brief scale pulse on success — skipped when prefers-reduced-motion
      transform: isAdded && !prefersReducedMotion ? "scale(1.03)" : "scale(1)",
    }

    return (
      <div style={wrapperStyle}>
        {inner}
        {/* interactive section — dark-glass inline styles */}
        <div
          style={{
            padding: "0 12px 14px",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {fits && (
            <div
              style={{
                fontSize: 11,
                color: "#fff",
                lineHeight: 1.3,
              }}
            >
              Fits {fits.model} ({fits.years})
            </div>
          )}
          {/* variant pills */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {variants.map((v, i) => {
              const isSelected = i === selIdx
              return (
                <button
                  key={v.id}
                  type="button"
                  disabled={!v.available}
                  onClick={() => setSelIdx(i)}
                  style={{
                    padding: "4px 10px",
                    fontSize: 11.5,
                    fontWeight: isSelected ? 600 : 400,
                    borderRadius: 20,
                    border: isSelected
                      ? "1.5px solid #6DC3F7"
                      : "1.5px solid rgba(255,255,255,0.25)",
                    background: isSelected ? "#6DC3F7" : "transparent",
                    color: isSelected ? "#fff" : "rgba(255,255,255,0.75)",
                    cursor: v.available ? "pointer" : "not-allowed",
                    opacity: v.available ? 1 : 0.4,
                    transition: "background 100ms, border-color 100ms",
                  }}
                >
                  {v.title}
                </button>
              )
            })}
          </div>
          {/* price row */}
          {price && (
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "rgba(255,255,255,0.9)",
              }}
            >
              {price}
            </div>
          )}
          {/* add to cart — with loading/success states */}
          <button
            type="button"
            disabled={!canAdd && !isAdding && !isAdded}
            onClick={() => { void handleAddToCart() }}
            style={addBtnStyle}
            // While the spinner shows, the button's only content is an
            // aria-hidden span → no accessible name (a11y audit 2026-06-11).
            aria-label={isAdding ? "Adding to cart" : undefined}
            aria-busy={isAdding || undefined}
          >
            {isAdding ? (
              <span
                aria-hidden="true"
                style={{
                  display: "inline-block",
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  border: "2px solid rgba(255,255,255,0.35)",
                  borderTopColor: "#fff",
                  animation: "spin 0.65s linear infinite",
                }}
              />
            ) : isAdded ? (
              "Added ✓"
            ) : (
              "Add to cart"
            )}
          </button>
        </div>
      </div>
    )
  }

  // ── no-variant path — UNCHANGED ──────────────────────────────────────
  if (data.productUrl) {
    return (
      <a
        href={data.productUrl}
        style={wrapperStyle}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(255,255,255,0.07)"
          e.currentTarget.style.borderColor = "rgba(109, 195, 247, 0.45)"
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "rgba(255,255,255,0.04)"
          e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"
        }}
      >
        {inner}
      </a>
    )
  }
  return <div style={wrapperStyle}>{inner}</div>
}
